/**
 * PO Line Detail Tab - extracted from POLineDetailModal.
 *
 * Renders the main detail view (left column: line fields, right column: changelog).
 */
import React, { useMemo } from 'react';
import { Calendar, Hash, DollarSign } from 'lucide-react';
import type { PurchaseOrderLine } from '../../lib/api';
import Changelog, { ChangelogEntry } from '../common/Changelog';

/**
 * CR-4731 round 2: collapse propose+decision row pairs into one row.
 *
 * The backend writes two POChangeLog rows per change-request lifecycle: one
 * for the proposal (orange "Change Request" badge) and one for the
 * accept/reject/cancel decision (green "Change Accepted" badge etc.). The
 * Audit History panel was rendering both, which read to users as the system
 * over-counting the number of changes (Sarah's PRO00222 line 3: 1 request +
 * 1 acceptance = 6 audit rows for 3 fields). Group by (field, ref) and keep
 * the most recent — the decision row when present (correct old/new values
 * after CR-4731 round 1, plus the right badge), or the proposal row while
 * the request is still open. Rows without a change_request_ref (legacy data,
 * GRN events, hold/critical changes) pass through unchanged.
 */
function collapseChangeRequestRows(entries: ChangelogEntry[]): ChangelogEntry[] {
  // First pass: bucket rows by (field, change_request_ref) so we can
  // identify pairs and pick the surviving row.
  const buckets = new Map<string, ChangelogEntry[]>();
  const passthrough: ChangelogEntry[] = [];
  for (const entry of entries) {
    if (entry.change_request_ref == null) {
      passthrough.push(entry);
      continue;
    }
    const key = `${entry.field_changed}::${entry.change_request_ref}`;
    const bucket = buckets.get(key) || [];
    bucket.push(entry);
    buckets.set(key, bucket);
  }

  // CR-4731 round 5: when a bucket has both a submission and a decision
  // row, keep the decision row as the surviving entry but attach the
  // submission event so the card shows "Submitted by X on Y" beneath
  // the outcome. Single-row buckets pass through unchanged.
  const grouped: ChangelogEntry[] = [];
  buckets.forEach(bucket => {
    const ordered = [...bucket].sort(
      (a, b) => new Date(a.change_date).getTime() - new Date(b.change_date).getTime()
    );
    const decision = [...ordered].reverse().find(r => {
      const reason = (r.reason || '').toLowerCase();
      return /\b(accepted|declined|rejected|cancelled)\b/.test(reason);
    });
    const submission = ordered.find(r => r !== decision);
    if (decision && submission) {
      grouped.push({
        ...decision,
        submission_event: {
          change_date: submission.change_date,
          changed_by: submission.changed_by,
          changed_by_company: submission.changed_by_company,
        },
      });
    } else {
      grouped.push(ordered[ordered.length - 1]);
    }
  });

  return [...grouped, ...passthrough].sort(
    (a, b) => new Date(b.change_date).getTime() - new Date(a.change_date).getTime()
  );
}

/* ------------------------------------------------------------------ */
/*  Shared helpers                                                     */
/* ------------------------------------------------------------------ */

/** Status badge configuration matching old portal display mapping. */
export const getAcknowledgeStatusBadge = (status?: string) => {
  if (!status) return null;

  const statusConfig: Record<string, { label: string; className: string }> = {
    new: { label: 'New', className: 'bg-blue-500 text-white' },
    declined: { label: 'Declined', className: 'bg-gray-800 text-white' },
    change_proposed_supplier: { label: 'Changed', className: 'bg-amber-500 text-white' },
    change_proposed_customer: { label: 'Changed', className: 'bg-amber-500 text-white' },
    wip: { label: 'WIP', className: 'bg-indigo-200 text-indigo-900' },
    accepted: { label: 'WIP', className: 'bg-green-500 text-white' },
    despatched: { label: 'Despatched', className: 'bg-gray-200 text-gray-900' },
    change_declined: { label: 'Change Declined', className: 'bg-red-500 text-white' },
    cancelled: { label: 'Cancelled', className: 'bg-gray-600 text-white' },
  };

  const config = statusConfig[status] || { label: status, className: 'bg-gray-200 text-gray-900' };

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.className}`}>
      {config.label}
    </span>
  );
};

// CR-3403: Interface for pending change
export interface PendingChange {
  field: string;
  label: string;
  oldValue: string;
  newValue: string;
  reason?: string;
  changeDate: string;
  changedBy: string;
  icon: React.ReactNode;
}

/** Get pending changes for review based on acknowledge_status. */
export function getPendingChanges(
  line: PurchaseOrderLine,
  changelogs: ChangelogEntry[],
  canShowWorkflow: boolean,
  userPlatform?: string,
  currentUserId?: number,
): PendingChange[] {
  if (!canShowWorkflow) return [];

  const pendingChanges: PendingChange[] = [];
  const status = line.acknowledge_status;

  // CR-4688: If acknowledge_status is no longer change_proposed_*, all
  // proposals have been fully processed — no pending changes to show.
  if (status !== 'change_proposed_supplier' && status !== 'change_proposed_customer') return [];

  const recentChanges = changelogs.filter(log => {
    const changeDate = new Date(log.change_date);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return changeDate >= thirtyDaysAgo;
  });

  const latestByField: Record<string, ChangelogEntry> = {};
  for (const log of recentChanges) {
    const field = log.field_changed;
    if (!latestByField[field] || new Date(log.change_date) > new Date(latestByField[field].change_date)) {
      latestByField[field] = log;
    }
  }

  const fieldConfig: Record<string, { label: string; icon: React.ReactNode }> = {
    delivery_date: { label: 'Customer Required Date', icon: <Calendar className="h-5 w-5" /> },
    supplier_promise_date: { label: 'Promise Date', icon: <Calendar className="h-5 w-5" /> },
    quantity: { label: 'Quantity', icon: <Hash className="h-5 w-5" /> },
    unit_price: { label: 'Unit Price', icon: <DollarSign className="h-5 w-5" /> },
  };

  for (const [field, config] of Object.entries(fieldConfig)) {
    const log = latestByField[field];
    if (log && log.old_value && log.new_value) {
      const isProposedChange = log.reason?.toLowerCase().includes('proposed') ||
        status === 'change_proposed_supplier' ||
        status === 'change_proposed_customer';

      if (isProposedChange) {
        // Only show changes from the OTHER party
        // 1. Skip if changed_by is the current user (you don't accept your own proposals)
        if (currentUserId && Number(log.changed_by) === currentUserId) continue;
        // 2. Check company_type on the changelog entry
        const changeCompanyType = (log as unknown as Record<string, unknown>)['company_type'] as string | undefined;
        const isCustomer = userPlatform === 'customer' || userPlatform === 'CUSTOMER' || userPlatform === 'HELIX';
        if (isCustomer && changeCompanyType === 'customer') continue;
        if (!isCustomer && changeCompanyType === 'supplier') continue;

        pendingChanges.push({
          field,
          label: config.label,
          oldValue: log.old_value,
          newValue: log.new_value,
          reason: log.reason,
          changeDate: log.change_date,
          changedBy: log.changed_by,
          icon: config.icon,
        });
      }
    }
  }

  return pendingChanges;
}

/** Get proposer name based on status. */
export function getProposerInfo(line: PurchaseOrderLine) {
  const status = line.acknowledge_status;
  if (status === 'change_proposed_supplier') {
    return { name: line.supplier_name || 'Supplier', type: 'supplier' };
  } else if (status === 'change_proposed_customer') {
    return { name: line.customer_name || 'Customer', type: 'customer' };
  }
  return { name: 'System', type: 'system' };
}

/* ------------------------------------------------------------------ */
/*  Detail Tab Component                                               */
/* ------------------------------------------------------------------ */

interface POLineDetailTabProps {
  line: PurchaseOrderLine;
  changelogs: ChangelogEntry[];
  userType: 'customer' | 'supplier';
  userId?: number;
  formatCurrency: (value: string, currency?: string) => string;
  formatDate: (dateString: string) => string;
  getChangeCount: (fieldName: string) => number | null;
  canShowWorkflowActions: boolean;
  /** CR-4705: Callback to navigate to change requests tab for review. */
  onReviewChanges?: () => void;
}

// CR-4711: Read-only field renderer used throughout the redesigned Detail
// tab. Keeps the visual style consistent and lets us pass an optional
// change-count badge or red-border (customer-only fields per the spec).
interface ReadOnlyFieldProps {
  label: string;
  value: string | undefined | null;
  badge?: number | null;
  redBorder?: boolean;
  withCalendarIcon?: boolean;
  primaryLabel?: boolean;
}

function ReadOnlyField({ label, value, badge, redBorder, withCalendarIcon, primaryLabel }: ReadOnlyFieldProps) {
  const display = value && value !== '' ? value : '-';
  return (
    <div>
      <label className={`block text-sm font-medium mb-1 ${primaryLabel ? 'text-primary' : 'text-muted-foreground'}`}>{label}</label>
      <div className={`flex items-center gap-2 px-3 py-2 border rounded-lg bg-muted/30 ${redBorder ? 'border-red-300' : 'border-border'}`}>
        {withCalendarIcon && <Calendar className="h-4 w-4 text-muted-foreground" />}
        <span className="flex-1 font-medium truncate">{display}</span>
        {badge ? (
          <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-xs font-semibold">{badge}</span>
        ) : null}
      </div>
    </div>
  );
}

export function POLineDetailTabContent({
  line,
  changelogs,
  userType,
  formatCurrency,
  formatDate,
  getChangeCount,
}: POLineDetailTabProps) {
  // CR-4711 round 2: aligned to Sarah's design (image dated May 2026).
  // Layout:
  //   Row A (header parties): Supplier Group | Supplier
  //   Row B (supplier sees only Customer here)
  //   Row C (customer-only): Order Date | Buyer ID
  //   Row D (parts):  Part Number(s) | Part Description | Drawing Issue†
  //   Row E (refs):   Batch/Serial No† | PnR Note No†       (customer-only)
  //   Row F (qtys):   Qty Ordered | Qty Delivered† | Qty O/S†
  //   Row G (dates):  Original Required Date | Supplier Promise Date | Customer Required Date
  //   Row H (price):  Unit Price | Currency† | Exchange Rate†   (customer-only)
  //   Row I (vals):   Total Value† | O/S Value†                 (customer-only)
  //   Row J (red):    Contract Unit Price | Contract Lot Price | Contract Price | PPV
  //   Row K (red):    PDF Attached | Mat Certs
  //
  // † = customer-only per the design's red-marked fields. Suppliers are
  // hidden from these entirely, not just blanked out — the FE should
  // show a slimmer pane on the supplier side rather than empty cells.
  //
  // Removed from the previous attempt:
  //   - "Agreed Date (ODD)" relabelled to "Original Required Date"
  //     (Sarah's annotations call the column "Agreed Date (ODD)" via the
  //     same source field, but the spec column header is "Original
  //     Required Date"). delivery_date is the source.
  //   - Duplicate "Latest Supplier Promise Date" — same source as
  //     Supplier Promise Date, dropped per design.
  //   - "MRP/ERP Scheduled" — not present in the new design.
  //   - Inline "Supplier Proposed Unit Price" — not in the new design;
  //     proposed values still surface via the Audit History changelog
  //     and the Change Requests tab.
  const isCustomer = userType === 'customer';
  // CR-4731 round 2: collapse propose+decision pairs so the Audit History
  // shows one row per change-request lifecycle, not two.
  const collapsedChangelogs = useMemo(() => collapseChangeRequestRows(changelogs), [changelogs]);
  const fmtQty = (q?: string | number | null) => (q === undefined || q === null || q === '') ? '-' : parseFloat(String(q)).toFixed(0);

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Left Column - Line Details */}
      <div className="card p-4 border rounded-lg">
        <div className="space-y-4">
          {/* Parties row */}
          {userType === 'supplier' && (
            <ReadOnlyField label="Customer" value={line.customer_name} />
          )}
          {isCustomer && (
            <div className="grid grid-cols-2 gap-3">
              {line.supplier_group_name ? (
                <ReadOnlyField label="Supplier Group" value={line.supplier_group_name} />
              ) : null}
              <ReadOnlyField label="Supplier" value={line.supplier_name} />
            </div>
          )}

          {/* CR-4711: Order Date + Buyer ID — customer-only per design red marks */}
          {isCustomer && (
            <div className="grid grid-cols-2 gap-3">
              <ReadOnlyField label="Order Date" value={line.order_date ? formatDate(line.order_date) : ''} withCalendarIcon redBorder />
              <ReadOnlyField label="Buyer ID" value={line.buyer_id} redBorder />
            </div>
          )}

          {/* Part identification: Part Number + Description (+ Drawing Issue customer-only) */}
          <div className={`grid ${isCustomer ? 'grid-cols-3' : 'grid-cols-2'} gap-3`}>
            <ReadOnlyField label="Part Number(s)" value={line.part_number} primaryLabel />
            <ReadOnlyField label="Part Description" value={line.description} />
            {isCustomer && (
              <ReadOnlyField label="Drawing Issue" value={line.drawing_version} redBorder />
            )}
          </div>

          {/* CR-4711: Batch/Serial No + PnR Note No — customer-only per design */}
          {isCustomer && (
            <div className="grid grid-cols-2 gap-3">
              <ReadOnlyField label="Batch/Serial No" value={line.batch_serial_no} redBorder />
              <ReadOnlyField label="PnR Note No" value={line.packing_note_number} redBorder />
            </div>
          )}

          {/* Quantities: Ordered always; Delivered + O/S customer-only per design */}
          <div className={`grid ${isCustomer ? 'grid-cols-3' : 'grid-cols-1'} gap-3`}>
            <ReadOnlyField label="Qty Ordered (Total)" value={fmtQty(line.quantity)} badge={getChangeCount('quantity')} primaryLabel />
            {isCustomer && (
              <>
                <ReadOnlyField label="Qty Delivered" value={fmtQty(line.received_qty)} redBorder />
                <ReadOnlyField label="Qty O/S" value={fmtQty(line.outstanding_qty)} redBorder />
              </>
            )}
          </div>

          {/* Dates: Original Required | Supplier Promise | Customer Required (single row per design) */}
          <div className="grid grid-cols-3 gap-3">
            <ReadOnlyField
              label="Original Required Date"
              value={line.delivery_date ? formatDate(line.delivery_date) : ''}
              withCalendarIcon
            />
            <ReadOnlyField
              label="Supplier Promise Date"
              value={line.supplier_promise_date ? formatDate(line.supplier_promise_date) : ''}
              badge={getChangeCount('supplier_promise_date')}
              withCalendarIcon
            />
            <ReadOnlyField
              label="Customer Required Date"
              value={line.delivery_date ? formatDate(line.delivery_date) : ''}
              badge={getChangeCount('delivery_date')}
              withCalendarIcon
              primaryLabel
            />
          </div>

          {/* Pricing — Unit Price visible to all; Currency + Exchange Rate customer-only */}
          <div className={`grid ${isCustomer ? 'grid-cols-3' : 'grid-cols-1'} gap-3`}>
            <ReadOnlyField
              label="Unit Price"
              value={formatCurrency(line.unit_price, line.currency)}
              badge={getChangeCount('unit_price')}
            />
            {isCustomer && (
              <>
                <ReadOnlyField label="Currency" value={line.currency} redBorder />
                <ReadOnlyField label="Exchange Rate" value={line.exchange_rate} redBorder />
              </>
            )}
          </div>

          {/* CR-4711: Customer-only commercial block per design red zone.
              All values hidden from suppliers entirely — bordered red on the
              customer side so reviewers can tell at a glance these are
              commercially-sensitive fields. */}
          {isCustomer && (
            <>
              {/* Totals row */}
              <div className="grid grid-cols-2 gap-3">
                <ReadOnlyField
                  label="Total Value"
                  value={line.total_price ? formatCurrency(line.total_price, line.currency) : ''}
                  redBorder
                />
                <ReadOnlyField
                  label="O/S Value"
                  value={line.outstanding_value ? formatCurrency(line.outstanding_value, line.currency) : ''}
                  redBorder
                />
              </div>

              {/* Contract block (4-col row per design) */}
              <div className="grid grid-cols-4 gap-3">
                <ReadOnlyField
                  label="Contract Unit Price"
                  value={line.contract_unit_price ? formatCurrency(line.contract_unit_price, line.currency) : ''}
                  redBorder
                />
                <ReadOnlyField
                  label="Contract Lot Price"
                  value={line.contract_lot_price ? formatCurrency(line.contract_lot_price, line.currency) : ''}
                  redBorder
                />
                <ReadOnlyField
                  label="Contract Price"
                  value={line.contract_price ? formatCurrency(line.contract_price, line.currency) : ''}
                  redBorder
                />
                <ReadOnlyField label="PPV" value={line.ppv} redBorder />
              </div>

              {/* PDF Attached + Mat Certs row */}
              <div className="grid grid-cols-2 gap-3">
                <ReadOnlyField
                  label="PDF Attached"
                  value={line.pdf_attached_count !== undefined && line.pdf_attached_count !== null ? String(line.pdf_attached_count) : ''}
                  redBorder
                />
                <ReadOnlyField
                  label="Mat Certs"
                  value={line.material_cert_count !== undefined && line.material_cert_count !== null ? String(line.material_cert_count) : ''}
                  redBorder
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right Column - Changelog */}
      <div>
        <Changelog entries={collapsedChangelogs} maxHeight="500px" />
      </div>
    </div>
  );
}
